import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import LimitMeter, { mb, type Limit } from './LimitMeter'
import { useVeil } from '../lib/useVeil'
import { maskName } from '../lib/security'
import { isUnlocked, encryptFile, decryptToBlob } from '../lib/vault'
import type { Media, Person, Tree } from '../lib/types'

interface Props {
  tree: Tree
  persons: Person[]
  canEdit: boolean
  onVaultPrompt: () => void
}

/** Renders media; vault items are fetched as ciphertext and decrypted client-side. */
function MediaThumb({ m, treeId, onOpen }: { m: Media; treeId: string; onOpen: (m: Media, url: string) => void }) {
  const [url, setUrl] = useState<string | null>(m.enc ? null : api.mediaUrl(m.id))
  const [locked, setLocked] = useState(false)

  useEffect(() => {
    let revoke: string | null = null
    if (m.enc) {
      if (!isUnlocked(treeId)) { setLocked(true); return }
      api.fetchMediaBuffer(m.id)
        .then(buf => decryptToBlob(treeId, buf, m.enc_iv || '', m.mime))
        .then(blob => { revoke = URL.createObjectURL(blob); setUrl(revoke) })
        .catch(() => setLocked(true))
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke) }
  }, [m, treeId])

  if (locked) {
    return (
      <div className="aspect-square rounded-xl bg-void-800 border border-gold-500/20 flex flex-col items-center justify-center text-center p-2">
        <span className="text-2xl mb-1">🔐</span>
        <span className="text-[10px] text-slate-500">Vault locked</span>
      </div>
    )
  }
  if (!url) {
    return <div className="aspect-square rounded-xl bg-void-800 animate-pulse" />
  }
  return (
    <button onClick={() => onOpen(m, url)} className="relative aspect-square rounded-xl overflow-hidden border border-white/10 group">
      {m.kind === 'document' ? (
        <span className="w-full h-full bg-white/[0.04] flex flex-col items-center justify-center gap-1 p-2">
          <span className="text-3xl">📄</span>
          <span className="text-[9px] text-slate-400 truncate w-full text-center">{m.filename}</span>
        </span>
      ) : m.kind === 'video' && !m.enc ? (
        <video src={url} className="w-full h-full object-cover" muted preload="metadata" />
      ) : m.kind === 'video' ? (
        <video src={url} className="w-full h-full object-cover" muted />
      ) : (
        <img src={url} alt={m.caption || m.filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
      )}
      {m.kind === 'video' && (
        <span className="absolute inset-0 flex items-center justify-center text-3xl drop-shadow">▶</span>
      )}
      {!!m.enc && <span className="absolute top-1.5 right-1.5 text-xs">🔐</span>}
      {m.caption && (
        <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent text-[10px] text-white px-2 py-1 truncate text-left">{m.caption}</span>
      )}
    </button>
  )
}

export default function GalleryTab({ tree, persons, canEdit, onVaultPrompt }: Props) {
  const [media, setMedia] = useState<Media[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [caption, setCaption] = useState('')
  const [personId, setPersonId] = useState('')
  const [toVault, setToVault] = useState(false)
  const [viewer, setViewer] = useState<{ m: Media; url: string } | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const veil = useVeil()

  // storage headroom, refreshed after anything that moves the number (D-144)
  const [room, setRoom] = useState<{ limit: Limit; plan: string; purchases: boolean } | null>(null)
  const loadRoom = () => api.getHeadroom()
    .then(h => { const l = h.limits.find(x => x.key === 'storage'); if (l) setRoom({ limit: l, plan: h.plan, purchases: h.purchases_enabled }) })
    .catch(() => {})

  const load = () => api.listMedia(tree.id).then(ms => setMedia(ms.filter(m => m.kind !== 'avatar'))).finally(() => setLoading(false))
  useEffect(() => { load(); loadRoom() }, [tree.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file: File) => {
    setBusy(true); setErr('')
    try {
      const kind = file.type.startsWith('video/') ? 'video' : (file.type === 'application/pdf' || !file.type.startsWith('image/')) ? 'document' : 'photo'
      // pre-check the size here — uploading a 40 MB scan in full and only then being
      // told it is too big wastes the upload and reads as a broken app (D-138)
      const CAP = kind === 'video' ? 95 : kind === 'document' ? 30 : 15
      if (file.size > CAP * 1024 * 1024) {
        setErr(`That ${kind} is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit for ${kind === 'photo' ? 'photos' : kind === 'video' ? 'videos' : 'documents'} is ${CAP} MB. Nothing was uploaded.`)
        setBusy(false); return
      }
      // ...and pre-check the QUOTA for the same reason. The server stays
      // authoritative (this number is stale the moment a second device uploads),
      // but there is no reason to push the bytes up the wire to be told no.
      if (room && room.limit.limit >= 0 && room.limit.used + file.size > room.limit.limit) {
        const left = Math.max(0, room.limit.limit - room.limit.used)
        setErr(`That ${kind} is ${(file.size / 1024 / 1024).toFixed(1)} MB and you have ${mb(left)} left. Nothing was uploaded — free some space (⚙ → Trash) and try again.`)
        setBusy(false); return
      }
      if (toVault) {
        if (!isUnlocked(tree.id)) { onVaultPrompt(); setBusy(false); return }
        const { blob, ivHex } = await encryptFile(tree.id, file)
        await api.uploadMedia({ file: blob, filename: file.name, treeId: tree.id, personId: personId || undefined, kind, caption, encIv: ivHex })
      } else {
        await api.uploadMedia({ file, filename: file.name, treeId: tree.id, personId: personId || undefined, kind, caption })
      }
      setCaption('')
      await load()
      loadRoom()
    } catch (e) {
      setErr(e instanceof Error ? e.message.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '').trim() : 'Upload failed')
    } finally { setBusy(false) }
  }

  const remove = async (m: Media) => {
    if (!confirm(`Move ${m.filename} to the trash? It can be restored from ⚙ settings → Trash for 30 days.`)) return
    await api.deleteMedia(m.id)
    loadRoom()
    setViewer(null)
    await load()
  }

  const toggleTag = async (m: Media, pid: string) => {
    const current = (m.tags || []).map(t => t.id)
    const next = current.includes(pid) ? current.filter(x => x !== pid) : [...current, pid]
    await api.updateMedia(m.id, { person_ids: next })
    const ms = await api.listMedia(tree.id)
    const fresh = ms.filter(x => x.kind !== 'avatar')
    setMedia(fresh)
    const updated = fresh.find(x => x.id === m.id)
    if (updated && viewer) setViewer({ ...viewer, m: updated })
  }

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-400" /></div>

  return (
    <div>
      {canEdit && (
        <div className="card-glass p-4 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-gold !py-2 text-sm">
              {busy ? 'Uploading…' : '⬆ Upload photo / video / document'}
            </button>
            <input ref={fileRef} type="file" accept="image/*,video/*,application/pdf" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = '' }} />
            <input value={caption} onChange={e => setCaption(e.target.value)} placeholder="Caption (optional)"
              className="input-dark !py-2 text-sm flex-1 min-w-[140px]" />
            <select value={personId} onChange={e => setPersonId(e.target.value)} className="input-dark !py-2 text-sm w-44">
              <option value="">Whole family</option>
              {persons.map(p => <option key={p.id} value={p.id}>{maskName(p, veil)}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer" title="End-to-end encrypted — only vault holders can view">
              <input type="checkbox" checked={toVault} onChange={e => setToVault(e.target.checked)} className="accent-gold-500" />
              🔐 Legacy Vault (E2EE)
            </label>
          </div>
          {err && <p className="text-xs text-red-400 mt-2">{err}</p>}
          {room && <LimitMeter limit={room.limit} plan={room.plan} purchasesEnabled={room.purchases} />}
          <p className="text-[10px] text-slate-600 mt-2">
            Photos ≤15MB · documents ≤30MB · videos ≤95MB (Heritage/Dynasty) · Vault items are AES-256-GCM encrypted on your device — the server never sees them.
          </p>
        </div>
      )}

      {media.length === 0 ? (
        <div className="card-glass p-12 text-center text-slate-500 text-sm">
          <div className="text-4xl mb-3">📸</div>
          The family album is empty — add the first memory.
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
          {media.map(m => <MediaThumb key={m.id} m={m} treeId={tree.id} onOpen={(mm, url) => setViewer({ m: mm, url })} />)}
        </div>
      )}

      {/* Lightbox */}
      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setViewer(null)}>
          <div className="max-w-4xl w-full" onClick={e => e.stopPropagation()}>
            {viewer.m.kind === 'video'
              ? <video src={viewer.url} controls autoPlay className="w-full max-h-[75vh] rounded-xl" />
              : viewer.m.kind === 'document'
                ? <iframe src={viewer.url} title={viewer.m.filename} className="w-full h-[75vh] rounded-xl bg-white" />
                : <img src={viewer.url} alt={viewer.m.caption} className="w-full max-h-[75vh] object-contain rounded-xl" />}
            <div className="flex items-center justify-between mt-3 text-sm text-slate-300">
              <div className="min-w-0">
                <p>{viewer.m.caption || viewer.m.filename} {!!viewer.m.enc && '· 🔐 vault'}</p>
                <p className="text-xs text-slate-500">{new Date(viewer.m.created_at + 'Z').toLocaleDateString()}</p>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {persons.map(p => {
                    const tagged = (viewer.m.tags || []).some(t => t.id === p.id) || viewer.m.person_id === p.id
                    if (!tagged && !canEdit) return null
                    return (
                      <button key={p.id} disabled={!canEdit || viewer.m.person_id === p.id}
                        onClick={() => toggleTag(viewer.m, p.id)}
                        title={canEdit ? (tagged ? 'Untag' : 'Tag in this memory') : undefined}
                        className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                          tagged ? 'border-gold-500/40 bg-gold-500/10 text-gold-300' : 'border-white/10 text-slate-500 hover:border-white/25 hover:text-slate-300'}`}>
                        {tagged ? '✓ ' : '+ '}{maskName(p, veil)}
                      </button>
                    )
                  })}
                </div>
              </div>
              <div className="flex gap-3">
                {canEdit && <button onClick={() => remove(viewer.m)} className="text-red-400/80 hover:text-red-400 text-xs">Delete</button>}
                <button onClick={() => setViewer(null)} className="btn-ghost !py-1.5 !px-3 text-xs">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
